import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import Icon from './Icon';

export type ToastKind = 'success' | 'error' | 'info';

interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastContextValue {
  toast: (message: string, kind?: ToastKind) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

const colors: Record<ToastKind, string> = {
  success: 'var(--green)',
  error:   'var(--red)',
  info:    'var(--ink)',
};

const icons: Record<ToastKind, string> = {
  success: 'check-circle',
  error:   'x-circle',
  info:    'bell',
};

let counter = 0;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [items, setItems] = useState<ToastItem[]>([]);

  const toast = useCallback((message: string, kind: ToastKind = 'success') => {
    counter += 1;
    const id = counter;
    setItems((prev) => [...prev, { id, kind, message }]);
    setTimeout(() => {
      setItems((prev) => prev.filter((t) => t.id !== id));
    }, 2800);
  }, []);

  const value = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Full-width strip on phones, top-right stack from sm up. */}
      <div
        className="fixed top-4 left-4 right-4 sm:left-auto sm:top-5 sm:right-5 flex flex-col gap-2"
        style={{ zIndex: 2000 }}
      >
        {items.map((t) => (
          <div
            key={t.id}
            className="sm:max-w-[380px]"
            style={{
              padding: '14px 20px',
              borderRadius: 12,
              fontSize: 14,
              fontWeight: 600,
              color: '#fff',
              background: colors[t.kind],
              boxShadow: '0 8px 24px rgba(0,0,0,.16)',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              animation: 'fadeIn .3s ease both',
            }}
          >
            <Icon name={icons[t.kind]} size={16} color="#fff" />
            <span>{t.message}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextValue => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
};
